import React, { useState } from "react";
import { useMatch } from "@tanstack/react-router";
import { differenceInYears, parse } from "date-fns";
import { CertificationsCard } from "@/features/profile/components/CertificationsCard";
import { WorkHistoryCard } from "@/features/profile/components/WorkHistoryCard";
import { StateLicensesCard } from "@/features/profile/components/StateLicensesCard";
import { AdmittingPrivilegesCard } from "@/features/profile/components/AdmittingPrivilegesCard";
import { EducationCard } from "@/features/profile/components/EducationCard";
import { ReferencesCard } from "@/features/profile/components/ReferencesCard";
import { StatsSection } from "@/features/profile/components/StatsSection";
import { PrimaryDetailsCard } from "@/features/profile/components/PrimaryDetailsCard";
import { SpecialtiesCard } from "./SpecialtiesCard";
import DocumentsCard from "./DocumentsCard";
import { JobPreferencesCard } from "./JobPreferencesCard";
import type { RouterOutputs } from "@/trpc";
import type {
  CredentialField,
  CredentialFieldEntry,
} from "@/features/credential-fields/types/cedential-field";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type SpecialtyCredentialValue = {
  specialty_code: string;
  initial_certification_date?: string | null;
  expiration_date?: string | null;
  certifying_board?: string | null;
  state?: string | null;
  certification_number?: string | null;
};

type NameCredentialValue = {
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  suffix_name?: string | null;
};

type AddressCredentialValue = {
  address_1: string;
  address_2?: string | null;
  city: string;
  state: string;
  zip_code: string;
};

type HealthcareRoleCredentialValue = {
  healthcare_role_id: string;
};

type ImlcCredentialValue = {
  has_license: boolean;
  state_codes: string[];
  expiration_date?: string | null;
};

type StateLicenseCredentialValue = {
  state: string | null;
  license_number: string | null;
  expiration_date: string | null;
};

type NonNullCredentialFieldEntry<T> = CredentialFieldEntry<T> & {
  value: T;
};

type CredentialsFields =
  RouterOutputs["credentialFiles"]["getCredentialsFields"];
type JobPreferences = RouterOutputs["profile"]["getJobPreferences"] | undefined;
type SharePermissions = RouterOutputs["profile"]["getPermissions"] | null;
type CredentialEditRequest =
  RouterOutputs["credentialFiles"]["getCredentialEditAccess"]["request"];

const getCredentialField = <T,>(field: unknown) => {
  return field as CredentialField<T> | undefined;
};

const getNonNullEntries = <T,>(
  field: CredentialField<T> | undefined,
): NonNullCredentialFieldEntry<T>[] => {
  return (
    field?.values?.filter(
      (entry): entry is NonNullCredentialFieldEntry<T> =>
        entry?.value !== null && entry?.value !== undefined,
    ) ?? []
  );
};

export type ProviderProfilePageProps = {
  user: {
    id: string;
    profileImagePath: string | null;
  };
  credentials: CredentialsFields;
  jobPreferences: JobPreferences;
  sharePermissions: SharePermissions;
  forceMobileView?: boolean;
  isMe?: boolean;
  allowDocumentUploads?: boolean;
  credentialEditRequest?: CredentialEditRequest;
  onRequestCredentialEditAccess?: (message?: string) => void;
  isRequestingCredentialEditAccess?: boolean;
  organizationBusinessUnitId?: string;
  isProviderHidden?: boolean;
  showChatButton?: boolean;
};

export const ProviderProfilePage = ({
  user,
  credentials,
  jobPreferences,
  sharePermissions,
  forceMobileView = false,
  isMe = false,
  allowDocumentUploads = false,
  credentialEditRequest,
  onRequestCredentialEditAccess,
  isRequestingCredentialEditAccess = false,
  organizationBusinessUnitId,
  isProviderHidden = false,
  showChatButton: showChatButtonProp,
}: ProviderProfilePageProps) => {
  const [editingSections, setEditingSections] = useState<string[]>([]);
  const [documentsPanelOpen, setDocumentsPanelOpen] = useState(false);
  const organizationRoute = useMatch({
    from: "/_org",
    shouldThrow: false,
  });

  const {
    name,
    address,
    work_experience,
    npi_number,
    professional_education,
    professional_training,
    fifth_pathway_education,
    undergraduate_education,
    imlc_license,
    state_medical_licenses,
    dea,
    cds,
    bls,
    acls,
    specialty,
    admitting_privileges,
    healthcare_role,
    linkedin_url,
    professional_references,
  } = credentials;

  const editable = Boolean(sharePermissions?.canEdit);
  const documentsEditable = isMe || allowDocumentUploads;
  const isOrganizationView = Boolean(
    organizationRoute || organizationBusinessUnitId,
  );
  const shareable =
    !isOrganizationView && Boolean(sharePermissions?.canReshare);

  const specialtyField =
    getCredentialField<SpecialtyCredentialValue>(specialty);
  const specialtyEntries = getNonNullEntries(specialtyField);
  const specialtyValues: SpecialtyCredentialValue[] = specialtyEntries.map(
    (entry) => entry.value,
  );
  const specialtyCodes = specialtyValues.map((s) => s.specialty_code);
  const specialtiesForCard: Array<{
    index: number;
    value: SpecialtyCredentialValue;
  }> = specialtyEntries.map((entry) => ({
    index: entry.index,
    value: entry.value,
  }));
  const nameField = getCredentialField<NameCredentialValue>(name);
  const nameValue = nameField?.values?.[0]?.value ?? null;
  const fullName = [
    nameValue?.first_name,
    nameValue?.middle_name,
    nameValue?.last_name,
  ]
    .filter(Boolean)
    .join(" ");
  const addressField = getCredentialField<AddressCredentialValue>(address);
  const addressValue = addressField?.values?.[0]?.value ?? null;
  const npiField = getCredentialField<string>(npi_number);
  const npiNumber = npiField?.values?.[0]?.value ?? null;

  const workExperiences = getNonNullEntries(
    getCredentialField<unknown>(work_experience),
  );
  const professionalEducation = getNonNullEntries(
    getCredentialField<unknown>(professional_education),
  );
  const professionalTraining = getNonNullEntries(
    getCredentialField<unknown>(professional_training),
  );
  const fifthPathwayEducation = getNonNullEntries(
    getCredentialField<unknown>(fifth_pathway_education),
  );
  const undergraduateEducation = getNonNullEntries(
    getCredentialField<unknown>(undergraduate_education),
  );
  const stateLicenses = getNonNullEntries(
    getCredentialField<StateLicenseCredentialValue>(state_medical_licenses),
  );
  const imlcField = getCredentialField<ImlcCredentialValue>(imlc_license);
  const imlcLicense = imlcField?.values?.[0]?.value ?? null;
  const deaCertification = getNonNullEntries(getCredentialField<unknown>(dea));
  const cdsCertification = getNonNullEntries(getCredentialField<unknown>(cds));
  const blsCertification = getNonNullEntries(getCredentialField<unknown>(bls));
  const aclsCertification = getNonNullEntries(
    getCredentialField<unknown>(acls),
  );
  const admittingPrivileges = getNonNullEntries(
    getCredentialField<unknown>(admitting_privileges),
  );
  const referencesList = getNonNullEntries(
    getCredentialField<unknown>(professional_references),
  );
  const healthcareProfessionalType =
    getCredentialField<HealthcareRoleCredentialValue>(healthcare_role)
      ?.values?.[0]?.value?.healthcare_role_id ?? null;
  const linkedinUrl =
    getCredentialField<string>(linkedin_url)?.values?.[0]?.value ?? null;

  const toggleEditSection = (section: string) => {
    setEditingSections((prev) => {
      if (prev.includes(section)) {
        return prev.filter((s) => s !== section);
      }
      return [...prev, section];
    });
  };

  const yearsOfExperience = (() => {
    if (workExperiences.length === 0) {
      return 0;
    }

    const earliestDate = workExperiences.reduce((earliest, exp) => {
      if (!(exp.value as { start_date: string }).start_date) {
        return earliest;
      }
      const startDate = parse(
        (exp.value as { start_date: string }).start_date,
        "yyyy-MM-dd",
        new Date(),
      );
      return startDate < earliest ? startDate : earliest;
    }, new Date());

    const years = differenceInYears(new Date(), earliestDate);
    return years;
  })();

  const certificationsCount =
    blsCertification.length +
    aclsCertification.length +
    cdsCertification.length +
    deaCertification.length;

  const hasJobPreferences = Boolean(jobPreferences);
  // Roster-only providers (marketplace job posts disabled) have no job
  // preferences — hide the card. Defaults to shown when there's no prefs row.
  const jobPostsEnabled = jobPreferences?.jobPostsEnabled ?? true;

  return (
    <>
      <div className="bg-white py-8">
        <div className="flex flex-col xl:flex-row gap-8 p-4 md:p-8">
          <div className="flex-1">
            <PrimaryDetailsCard
              editable={editable}
              shareable={shareable}
              userId={user.id}
              profileImagePath={user.profileImagePath}
              isEditing={editingSections.includes("primary-details")}
              onEditChange={() => toggleEditSection("primary-details")}
              showChatButton={showChatButtonProp ?? !isMe}
              address1={addressValue?.address_1 ?? undefined}
              address2={addressValue?.address_2 ?? undefined}
              city={addressValue?.city ?? undefined}
              state={addressValue?.state ?? undefined}
              zipCode={addressValue?.zip_code ?? undefined}
              firstName={nameValue?.first_name}
              lastName={nameValue?.last_name}
              middleName={nameValue?.middle_name ?? undefined}
              suffixName={nameValue?.suffix_name ?? undefined}
              specialtyCodes={specialtyCodes}
              npiNumber={npiNumber}
              isProviderHidden={isProviderHidden}
              healthcareProfessionalType={
                healthcareProfessionalType ?? undefined
              }
              linkedinUrl={linkedinUrl}
              onViewDocuments={() => setDocumentsPanelOpen(true)}
            />

            <StatsSection
              yearsOfExperience={yearsOfExperience}
              certificationsCount={certificationsCount}
              stateLicensesCount={stateLicenses.length}
            />

            <SpecialtiesCard
              editable={editable}
              userId={user.id}
              isEditing={editingSections.includes("specialties")}
              onEditChange={() => toggleEditSection("specialties")}
              specialties={specialtiesForCard}
              isProviderHidden={isProviderHidden}
            />

            {jobPostsEnabled && (
              <div className={`${forceMobileView ? "" : "xl:hidden"} my-4`}>
                <JobPreferencesCard
                  noPreferences={!hasJobPreferences}
                  editable={editable}
                  userId={user.id}
                  isEditing={editingSections.includes("job-preferences")}
                  onEditChange={() => toggleEditSection("job-preferences")}
                  searchStatus={jobPreferences?.searchStatusId}
                  preferredLocations={jobPreferences?.cities?.map(
                    (city) => city.name,
                  )}
                  practiceSettings={jobPreferences?.practiceSettings ?? []}
                  employmentTypes={jobPreferences?.employmentTypes ?? []}
                  minHourlyRate={jobPreferences?.minHourlyRate}
                  availability={jobPreferences?.availabilityDescription}
                />
              </div>
            )}

            <StateLicensesCard
              editable={editable}
              userId={user.id}
              isEditing={editingSections.includes("state-licenses")}
              onEditChange={() => toggleEditSection("state-licenses")}
              stateLicenses={stateLicenses}
              imlcLicense={imlcLicense}
              isProviderHidden={isProviderHidden}
            />

            <CertificationsCard
              editable={editable}
              userId={user.id}
              certifications={{
                bls: blsCertification,
                acls: aclsCertification,
                cds: cdsCertification,
                dea: deaCertification,
              }}
              isEditing={editingSections.includes("certifications")}
              onEditChange={() => toggleEditSection("certifications")}
              isProviderHidden={isProviderHidden}
            />

            <AdmittingPrivilegesCard
              editable={editable}
              userId={user.id}
              admittingPrivileges={admittingPrivileges}
              isEditing={editingSections.includes("admitting-privileges")}
              onEditChange={() => toggleEditSection("admitting-privileges")}
            />

            <WorkHistoryCard
              editable={editable}
              userId={user.id}
              isEditing={editingSections.includes("work-history")}
              onEditChange={() => toggleEditSection("work-history")}
              workExperiences={workExperiences}
            />

            <EducationCard
              editable={editable}
              userId={user.id}
              isEditing={editingSections.includes("education")}
              onEditChange={() => toggleEditSection("education")}
              education={{
                professional_training: professionalTraining,
                professional_education: professionalEducation,
                fifth_pathway_education: fifthPathwayEducation,
                undergraduate_education: undergraduateEducation,
              }}
            />

            <ReferencesCard
              editable={editable}
              userId={user.id}
              references={referencesList}
              isEditing={editingSections.includes("references")}
              onEditChange={() => toggleEditSection("references")}
            />
          </div>

          {jobPostsEnabled && (
            <div
              className={`w-full xl:w-96 ${forceMobileView ? "hidden" : "hidden xl:block"}`}
            >
              <JobPreferencesCard
                editable={editable}
                userId={user.id}
                isEditing={editingSections.includes("job-preferences")}
                onEditChange={() => toggleEditSection("job-preferences")}
                searchStatus={jobPreferences?.searchStatusId}
                preferredLocations={jobPreferences?.cities?.map(
                  (city) => city.name,
                )}
                practiceSettings={jobPreferences?.practiceSettings ?? []}
                employmentTypes={jobPreferences?.employmentTypes ?? []}
                minHourlyRate={jobPreferences?.minHourlyRate}
                availability={jobPreferences?.availabilityDescription}
                noPreferences={!hasJobPreferences}
              />
            </div>
          )}
        </div>
      </div>
      <Sheet open={documentsPanelOpen} onOpenChange={setDocumentsPanelOpen}>
        <SheetContent
          side={forceMobileView ? "bottom" : "right"}
          id="documents-panel"
          className="w-full h-full sm:max-w-xl lg:max-w-3xl overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Credentialing documents</SheetTitle>
            <SheetDescription>
              Everything you need to review, download, or upload provider
              documents without cluttering the profile.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 pb-12">
            <DocumentsCard
              userId={user.id}
              userName={fullName}
              editable={documentsEditable}
              allowUploads={documentsEditable}
              credentialEditRequest={credentialEditRequest}
              onRequestCredentialEditAccess={onRequestCredentialEditAccess}
              isRequestingCredentialEditAccess={
                isRequestingCredentialEditAccess
              }
              showCredentialAccessControls={isMe}
              organizationBusinessUnitId={organizationBusinessUnitId}
              containerClassName="bg-transparent mt-0"
              contentClassName="p-0"
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
